// ─── Chain config & contract addresses ──────────────────

import type { KnownCurrency } from "@/lib/types";
import { getActiveChainId, SUPPORTED_CHAINS } from "./activeChain";
import { AMOY_FALLBACK_RPC_URL, resolveAmoyRpcUrl } from "./rpcUrls";
import {
  AMOY_CONTRACTS,
  AMOY_TOKENS,
  POLYGON_CONTRACTS,
  POLYGON_TOKENS,
  type ContractAddresses,
} from "../../supabase/functions/_shared/contracts";

export type { ContractAddresses };

export interface ChainConfig {
  chainId: number;
  name: string;
  rpcUrl: string;
  blockExplorer: string;
  contracts: ContractAddresses;
  tokenAddresses: Record<KnownCurrency, string>;
}

const AMOY_CONFIG: ChainConfig = {
  chainId: 80002,
  name: "Polygon Amoy",
  rpcUrl:
    resolveAmoyRpcUrl(import.meta.env.VITE_POLYGON_RPC_URL) ?? AMOY_FALLBACK_RPC_URL,
  blockExplorer: "https://amoy.polygonscan.com",
  contracts: AMOY_CONTRACTS,
  tokenAddresses: AMOY_TOKENS,
};

const POLYGON_CONFIG: ChainConfig = {
  chainId: 137,
  name: "Polygon",
  // Mainnet has its own RPC var; VITE_POLYGON_RPC_URL is the Amoy endpoint
  // (see .env.example) and reading mainnet state through it returns nothing.
  rpcUrl: import.meta.env.VITE_POLYGON_MAINNET_RPC_URL ?? "https://polygon-rpc.com",
  blockExplorer: "https://polygonscan.com",
  contracts: POLYGON_CONTRACTS,
  tokenAddresses: POLYGON_TOKENS,
};

const CHAIN_CONFIGS: Record<number, ChainConfig> = {
  80002: AMOY_CONFIG,
  137: POLYGON_CONFIG,
};

// The switch renders from SUPPORTED_CHAINS while contract resolution happens
// here; if the two ever disagree, the UI offers a chain that throws on use.
if (import.meta.env.DEV) {
  for (const chain of SUPPORTED_CHAINS) {
    if (!CHAIN_CONFIGS[chain.id]) {
      console.error(
        `[contracts] SUPPORTED_CHAINS lists chain ${chain.id} but CHAIN_CONFIGS has no entry for it — the network switch would offer a chain that cannot resolve contracts.`,
      );
    }
  }
}

export function getChainConfig(chainId?: number): ChainConfig {
  // No explicit chain means "whatever the app is running against" -- the
  // admin's top-bar selection when there is one, VITE_POLYGON_CHAIN_ID otherwise.
  const resolved = chainId ?? getActiveChainId();
  const config = CHAIN_CONFIGS[resolved];
  if (!config) throw new Error(`Unsupported chain ID: ${resolved}`);
  return config;
}

// ─── Token helpers (6 decimals — works for USDC and USDT on Polygon) ──

export const USDC_DECIMALS = 6;

export function parseUSDC(amount: number): bigint {
  // Avoid floating-point precision issues by using string manipulation
  const [whole, frac = ""] = amount.toString().split(".");
  const padded = (frac + "000000").slice(0, USDC_DECIMALS);
  return BigInt(whole + padded);
}

export function formatUSDC(raw: bigint): number {
  const divisor = BigInt(10 ** USDC_DECIMALS);
  const whole = raw / divisor;
  const frac = raw % divisor;
  return Number(whole) + Number(frac) / 10 ** USDC_DECIMALS;
}
